/**
 * processTierLayout — LOCKED 2-D grid for the production model DAG, mirroring
 * the plan-graph's fixed workspace (PlanGraphView.layoutPlanGrid).
 *
 *   - x-axis = node TYPE column, canonical order: input_data · parameter ·
 *     transform · calculate · validate · output. Columns with zero nodes are
 *     skipped, so only the present types render (left→right). A frozen header
 *     labels each column with its type colour (getNodeTypeColor).
 *   - y-axis = dependency TIER (longest-path depth). Row 1 / top = nodes with no
 *     upstream dependency. Neutral zebra lane bands give the rows structure (like
 *     the plan graph's step lanes); a frozen left gutter labels each tier
 *     ("No dependencies" for tier 1, else "Tier N").
 *   - A node sits at (its type column, its dependency-tier row). When several
 *     nodes share a (column,tier) cell they stack vertically inside the lane
 *     (per-column stacking, mirroring layoutPlanGrid); the lane height expands.
 *
 * Node colour stays per-type (ModelingNode); the lanes are neutral. Pure +
 * deterministic — replaces ELK for this view, so there is nothing to drag and
 * nothing to "reset". The viewport is pinned/locked by ProcessDAG.
 *
 * Decoration node types are named 'tierLane' (zebra row band) and 'columnBand'
 * (full-height column divider) to match ProcessDAG's click/double-click guards.
 */
import type { Node, Edge } from 'reactflow';
import { MODELING_NODE_WIDTH, MODELING_NODE_HEIGHT } from './ModelingNode';
import { GRAPH_LAYER } from '../cluster/clusterLayout';
import type { ModelingNodeType } from '../../types/modeling';
import { getNodeTypeColor, getNodeTypeLabel } from '../../types/modeling';

// Canonical type-column order (x-axis, left→right). Columns absent from the
// model are dropped before layout, so the grid is dense.
const TYPE_COLUMN_ORDER: ModelingNodeType[] = [
  'input_data', 'parameter', 'transform', 'calculate', 'validate', 'output',
];
const TYPE_COLUMN_INDEX: Record<string, number> = {
  input_data: 0, parameter: 1, transform: 2, calculate: 3, validate: 4, output: 5,
};

// Grid geometry (world coordinates) — mirrors the plan grid's COLUMN_WIDTH /
// CARD_WIDTH / COLUMN_PAD / ROW_STEP relationship, sized for the modeling card.
export const COLUMN_WIDTH = MODELING_NODE_WIDTH + 72; // 184 + 72 = 256
export const COLUMN_PAD = (COLUMN_WIDTH - MODELING_NODE_WIDTH) / 2; // 36
export const ROW_STEP = MODELING_NODE_HEIGHT + 36; // 42 card + 36 gap between stacked cards
const LANE_TOP_PAD = 16;
const LANE_PAD_BOTTOM = 16;

export interface TierLaneData {
  width: number;
  height: number;
  shaded: boolean;
  label: string;
}

export interface ColumnBandData {
  width: number;
  height: number;
}

/** Frozen-header column descriptor (one per PRESENT type column). */
export interface TypeColumnMeta {
  type: ModelingNodeType;
  label: string;
  color: string;
  /** world x of the column's left edge. */
  left: number;
  width: number;
  index: number;
}

/** Frozen-gutter row descriptor (one per dependency tier + zebra fillers). */
export interface TierBandMeta {
  key: string;
  label: string;
  /** world y of the band's top edge. */
  top: number;
  height: number;
  shaded: boolean;
  index: number;
}

/** The React Flow node/edge objects ProcessDAG builds before layout — like
 *  reactflow's Node/Edge but without a computed `position` yet. */
type RawModelNode = {
  id: string;
  type: string;
  width?: number;
  height?: number;
  cluster_id?: string | null;
  data: { nodeType: ModelingNodeType };
  selected?: boolean;
};
type RawModelEdge = {
  id: string;
  source: string;
  target: string;
  type?: string;
  data?: unknown;
};

export interface TierLayoutResult {
  rfNodes: Node[];
  rfEdges: Edge[];
  /** Present type columns, left→right — for the frozen column header. */
  columns: TypeColumnMeta[];
  /** Dependency-tier bands (real tiers only) — for the frozen step gutter. */
  tiers: TierBandMeta[];
  gridWidth: number;
  gridHeight: number;
}

/**
 * Longest-path layering: tier(root)=0; tier(n)=max(tier(predecessors))+1. A valid
 * model is acyclic; if a cycle ever slips through, unresolved nodes degrade to
 * tier 0 rather than looping.
 */
function computeTiers(ids: string[], edges: { source: string; target: string }[]): Map<string, number> {
  const present = new Set(ids);
  const adj = new Map<string, string[]>();
  const indeg = new Map<string, number>();
  ids.forEach((id) => { adj.set(id, []); indeg.set(id, 0); });
  edges.forEach((e) => {
    if (!present.has(e.source) || !present.has(e.target) || e.source === e.target) return;
    adj.get(e.source)!.push(e.target);
    indeg.set(e.target, (indeg.get(e.target) || 0) + 1);
  });
  const tier = new Map<string, number>();
  const remaining = new Map(indeg);
  const queue: string[] = [];
  ids.forEach((id) => { if ((indeg.get(id) || 0) === 0) { tier.set(id, 0); queue.push(id); } });
  while (queue.length) {
    const u = queue.shift()!;
    const tu = tier.get(u) || 0;
    (adj.get(u) || []).forEach((v) => {
      tier.set(v, Math.max(tier.get(v) ?? 0, tu + 1));
      remaining.set(v, (remaining.get(v) || 0) - 1);
      if ((remaining.get(v) || 0) === 0) queue.push(v);
    });
  }
  ids.forEach((id) => { if (!tier.has(id)) tier.set(id, 0); });
  return tier;
}

function typeColumnIndex(type: ModelingNodeType): number {
  return TYPE_COLUMN_INDEX[type] ?? 0;
}

/**
 * Position model nodes on the locked 2-D grid (type column × dependency-tier
 * row) + emit the column-divider and zebra-lane decoration nodes. `rawNodes` /
 * `rawEdges` are the React Flow node/edge objects ProcessDAG already builds
 * (just without positions); we return them positioned + the decoration nodes +
 * the column/tier metadata ProcessDAG needs for the frozen header + gutter.
 */
export function layoutProductionTiers(rawNodes: RawModelNode[], rawEdges: RawModelEdge[]): TierLayoutResult {
  if (rawNodes.length === 0) {
    return { rfNodes: [], rfEdges: [], columns: [], tiers: [], gridWidth: 0, gridHeight: 0 };
  }

  const ids = rawNodes.map((n) => n.id);
  const tiers = computeTiers(ids, rawEdges.map((e) => ({ source: e.source, target: e.target })));
  const orderIndex = new Map<string, number>();
  rawNodes.forEach((n, i) => orderIndex.set(n.id, i));

  // ---- X-axis: present type columns in canonical order ----------------------
  const presentTypes = new Set<ModelingNodeType>();
  rawNodes.forEach((n) => presentTypes.add(n.data.nodeType));
  const columns: TypeColumnMeta[] = TYPE_COLUMN_ORDER
    .filter((t) => presentTypes.has(t))
    .map((t, index) => ({
      type: t,
      label: getNodeTypeLabel(t),
      color: getNodeTypeColor(t),
      left: index * COLUMN_WIDTH,
      width: COLUMN_WIDTH,
      index,
    }));
  // Map a node's canonical type-column to its packed (skip-empty) column index.
  const packedColumnIndex = new Map<ModelingNodeType, number>();
  columns.forEach((c) => packedColumnIndex.set(c.type, c.index));
  const gridWidth = columns.length * COLUMN_WIDTH;

  // ---- Y-axis: dependency tiers (rows), skipping empty tiers ----------------
  // Group nodes by tier, then within a tier bucket them by their type column so
  // a (column,tier) cell can stack its members vertically.
  const tierKeysPresent = Array.from(new Set(rawNodes.map((n) => tiers.get(n.id) ?? 0)))
    .sort((a, b) => a - b);

  const cardNodes: Node[] = [];
  const laneNodes: Node[] = [];
  const tierMeta: TierBandMeta[] = [];

  let y = 0;
  tierKeysPresent.forEach((tierValue, rowIdx) => {
    const members = rawNodes.filter((n) => (tiers.get(n.id) ?? 0) === tierValue);

    // Bucket this tier's nodes by their (packed) type column.
    const byColumn = new Map<number, RawModelNode[]>();
    members.forEach((n) => {
      const c = packedColumnIndex.get(n.data.nodeType) ?? 0;
      const arr = byColumn.get(c) || [];
      arr.push(n);
      byColumn.set(c, arr);
    });

    // Lane height grows with the tallest stacked cell (mirror layoutPlanGrid).
    let maxStack = 1;
    byColumn.forEach((cellNodes) => {
      cellNodes.sort((a, b) => (orderIndex.get(a.id) ?? 0) - (orderIndex.get(b.id) ?? 0));
      maxStack = Math.max(maxStack, cellNodes.length);
    });
    const laneHeight = LANE_TOP_PAD + maxStack * ROW_STEP + LANE_PAD_BOTTOM;

    laneNodes.push({
      id: `tier_lane_${tierValue}`,
      type: 'tierLane',
      position: { x: 0, y },
      data: {
        width: gridWidth,
        height: laneHeight,
        shaded: rowIdx % 2 === 0,
        label: rowIdx === 0 ? 'No dependencies' : `Tier ${rowIdx + 1}`,
      } as TierLaneData,
      draggable: false,
      selectable: false,
      connectable: false,
      zIndex: 0,
      style: { width: gridWidth, height: laneHeight, pointerEvents: 'none' },
    });

    byColumn.forEach((cellNodes, c) => {
      cellNodes.forEach((n, i) => {
        cardNodes.push({
          ...n,
          position: { x: c * COLUMN_WIDTH + COLUMN_PAD, y: y + LANE_TOP_PAD + i * ROW_STEP },
          draggable: false,
          zIndex: GRAPH_LAYER.card,
        } as Node);
      });
    });

    tierMeta.push({
      key: `tier_${tierValue}`,
      label: rowIdx === 0 ? 'No dependencies' : `Tier ${rowIdx + 1}`,
      top: y,
      height: laneHeight,
      shaded: rowIdx % 2 === 0,
      index: rowIdx,
    });
    y += laneHeight;
  });

  const gridHeight = y;

  // Column-divider decoration nodes: full-height hairline at each column's left
  // edge (named 'columnBand' to match the ProcessDAG guards). Spans the grid
  // height so the dividers reach the bottom of the content.
  const columnNodes: Node[] = columns.map((col) => ({
    id: `col_band_${col.index}`,
    type: 'columnBand',
    position: { x: col.left, y: 0 },
    data: { width: COLUMN_WIDTH, height: gridHeight } as ColumnBandData,
    draggable: false,
    selectable: false,
    connectable: false,
    zIndex: 0,
    style: { width: COLUMN_WIDTH, height: gridHeight, pointerEvents: 'none' },
  }));

  const rfEdges: Edge[] = rawEdges.map((e) => ({ ...e, zIndex: GRAPH_LAYER.edgeResting } as Edge));

  return {
    rfNodes: [...laneNodes, ...columnNodes, ...cardNodes],
    rfEdges,
    columns,
    tiers: tierMeta,
    gridWidth,
    gridHeight,
  };
}
